import React, { useEffect, useRef, useState } from 'react';
import { Loader2, MessageSquare, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';
import http, { API } from '../api/http';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

const errorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  return (typeof detail === 'string' ? detail : detail?.message) || fallback;
};

export default function MissionComments({ scheduleId, worksiteId, currentUserId }) {
  return <Discussion key={worksiteId ? `worksite:${worksiteId}` : `schedule:${scheduleId}`} scheduleId={scheduleId} worksiteId={worksiteId} currentUserId={currentUserId} />;
}

function Discussion({ scheduleId, worksiteId, currentUserId }) {
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [beforeId, setBeforeId] = useState(null);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const alive = useRef(false);
  const sendingRef = useRef(false);
  const lastReadId = useRef(null);
  const messagesRef = useRef(null);
  const followLatest = useRef(true);
  const url = worksiteId
    ? `${process.env.REACT_APP_API_BASE_URL || API}/worksites/${worksiteId}/comments`
    : `${process.env.REACT_APP_API_BASE_URL || API}/schedules/${scheduleId}/comments`;

  const mergeComments = (items) => setComments(previous => {
    const byId = new Map(previous.map(comment => [comment.id, comment]));
    items.forEach(comment => byId.set(comment.id, comment));
    return [...byId.values()].sort((left, right) => left.id - right.id);
  });

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (followLatest.current && messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [comments]);

  useEffect(() => {
    let cancelled = false;
    let timer;
    let initial = true;
    const controller = new AbortController();
    const load = async () => {
      try {
        if (!document.hidden || initial) {
          const response = await http.get(url, { signal: controller.signal });
          if (cancelled) return;
          let items = response.data.items;
          let cursor = response.data.next_before;
          while (lastReadId.current !== null && cursor && items[0]?.id > lastReadId.current) {
            const older = await http.get(url, { params: { before_id: cursor }, signal: controller.signal });
            if (cancelled) return;
            items = [...older.data.items, ...items];
            cursor = older.data.next_before;
          }
          mergeComments(items);
          if (lastReadId.current === null) setBeforeId(response.data.next_before);
          lastReadId.current = Math.max(lastReadId.current || 0, ...items.map(comment => comment.id));
          setError('');
        }
      } catch (failure) {
        if (!cancelled) setError(errorMessage(failure, 'Impossible de charger la discussion.'));
      } finally {
        if (!cancelled) {
          initial = false;
          setLoading(false);
          timer = setTimeout(load, 10000);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [url, refresh]);

  const loadOlder = async () => {
    setLoadingOlder(true);
    try {
      const response = await http.get(url, { params: { before_id: beforeId } });
      if (!alive.current) return;
      followLatest.current = false;
      mergeComments(response.data.items);
      setBeforeId(response.data.next_before);
    } catch (failure) {
      if (alive.current) toast.error(errorMessage(failure, 'Impossible de charger les anciens messages.'));
    } finally {
      if (alive.current) setLoadingOlder(false);
    }
  };

  const send = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || body.length > 2000 || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const response = await http.post(url, { body });
      if (!alive.current) return;
      followLatest.current = true;
      mergeComments([response.data]);
      setDraft('');
    } catch (failure) {
      if (alive.current) toast.error(errorMessage(failure, 'Impossible d\u2019envoyer le message.'));
    } finally {
      sendingRef.current = false;
      if (alive.current) setSending(false);
    }
  };

  return (
    <section aria-label="Discussion de la mission" className="min-w-0 border-t border-gray-200 pt-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-bold text-gray-900">
          <MessageSquare className="h-5 w-5 text-blue-600 shrink-0" /> Discussion
        </h3>
        <Button type="button" variant="ghost" size="icon" title="Actualiser la discussion" aria-label="Actualiser la discussion" onClick={() => setRefresh(value => value + 1)}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {loading ? <p role="status" className="text-sm text-gray-500">Chargement des messages...</p> : (
        <div ref={messagesRef} onScroll={event => {
          const element = event.currentTarget;
          followLatest.current = element.scrollHeight - element.scrollTop - element.clientHeight < 60;
        }} className="max-h-80 overflow-y-auto space-y-3 pr-1" aria-label="Messages">
          {beforeId && <Button type="button" variant="ghost" size="sm" disabled={loadingOlder} onClick={loadOlder}>Messages plus anciens</Button>}
          {!comments.length && !error && <p className="py-3 text-sm text-gray-500">Aucun message pour le moment.</p>}
          {comments.map(comment => (
            <article key={comment.id} className={`border-l-2 pl-3 py-1 min-w-0 ${comment.author_id === currentUserId ? 'border-blue-500' : 'border-gray-200'}`}>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs">
                <span className="font-semibold text-gray-900 break-words [overflow-wrap:anywhere]">{comment.author_name}</span>
                <span className="text-gray-500">{['ADMIN', 'BUREAU'].includes(comment.author_role) ? 'Bureau' : 'Technicien'}</span>
                <time dateTime={comment.created_at} className="text-gray-400">{new Date(comment.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</time>
              </div>
              <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap [overflow-wrap:anywhere]">{comment.body}</p>
            </article>
          ))}
        </div>
      )}
      <form onSubmit={send} className="space-y-2">
        <Textarea aria-label="Votre message" placeholder="Votre message..." value={draft} onChange={event => setDraft(event.target.value)} maxLength={2000} rows={3} disabled={sending} className="resize-y rounded-lg" />
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-gray-400">{draft.length}/2000</span>
          <Button type="submit" disabled={!draft.trim() || sending || loading} className="gap-2">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Envoi...' : 'Envoyer'}
          </Button>
        </div>
      </form>
    </section>
  );
}